import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetAllVQAList,
  useGetAuditorsListForFilters,
  useGetListForSponsorFilters,
  UseGetRfiListForFilters,
} from "../../custom-hooks/queries";
import useRequestListStickyFilters from "../../custom-hooks/useRequestListStickyFilters";
import SkeletonLoader from "../../components/SkeletonLoader";
import Pagination from "../../components/CustomPagination";
import Notification from "../../components/Notification";
import PageToolbarFilters, {
  defaultGetOptionValue,
} from "../../components/Filters/PageToolbarFilters";
import ToolbarSearch from "../../components/Filters/ToolbarSearch";
import TableExportMenu from "../../components/Menus/TableExportMenu";
import BackendAssessmentRequestGridTable from "../../components/Assessments/BackendAssessmentRequestGridTable";
import { assessmentCellStyles } from "../../utils/exportBadgeStyles";
import { generateV2ServiceCategoryOptions } from "../../utils/serviceCategoryV2Mapping";
import { setSearchTextByPage } from "../../redux-store/features/providerFiltersSlice";
import { MY_RFI_GXP_FILTER_OPTIONS } from "../../utils/myRfiGridRowMapper";
import {
  mapBackendAssessmentRequestItemToExportRecord,
  BACKEND_ASSESSMENT_REQUEST_EXPORT_HEADERS,
  sortBackendAssessmentRequestConfigToApiSortParams,
  sortBackendAssessmentRequestItems,
  filterAssessmentRequestItemsByClientFilters,
  ASSESSMENT_REQUEST_TYPE_FILTER_OPTIONS,
  ASSESSMENT_REQUEST_ONSITE_REMOTE_FILTER_OPTIONS,
  ASSESSMENT_REQUEST_MULTI_SPONSOR_FILTER_OPTIONS,
  VQA_LIST_USE_SERVER_SORT,
} from "../BackendUser/backendAssessmentRequestGridMapper";
import { normalizeVqaListItemForAssessmentGrid } from "../../utils/vqaListItemNormalizer";
import { getVqaListStatusFilterPayload } from "../../utils/vqaListStatusPayload";
import { REQUEST_LIST_SCREEN_KEYS } from "../../utils/requestListStickyFilterPersistence";

const PROVIDER_ASSESSMENT_PAGE_KEY = "providerAssessmentRequests";

const PROVIDER_ASSESSMENT_EMPTY_SELECTED_IDS = {
  sponsor: [],
  type: [],
  gxp: [],
  services: [],
  onsiteRemote: [],
  auditor: [],
  multiSponsor: [],
  status: [],
};

const PROVIDER_ASSESSMENT_DEFAULT_SORT = {
  requestDate: { direction: "desc" },
};

export const ProviderAssessmentGrid = () => {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const searchTimerRef = useRef(null);

  const { data: sponsors } = useGetListForSponsorFilters();
  const { data: rfis } = UseGetRfiListForFilters();
  const { data: auditors } = useGetAuditorsListForFilters();

  const searchQuery =
    useSelector(
      (state) =>
        state.providersFilters.searchTextByPage[PROVIDER_ASSESSMENT_PAGE_KEY]
    ) || "";

  const {
    selectedIds,
    setSelectedIds,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    sortFetchNonce,
  } = useRequestListStickyFilters({
    screenKey: REQUEST_LIST_SCREEN_KEYS.VENDOR_ASSESSMENTS,
    searchQuery,
    useServerSort: VQA_LIST_USE_SERVER_SORT,
    emptySelectedIds: PROVIDER_ASSESSMENT_EMPTY_SELECTED_IDS,
    defaultSortConfig: PROVIDER_ASSESSMENT_DEFAULT_SORT,
  });

  const v2ServiceOptions = useMemo(
    () => generateV2ServiceCategoryOptions(rfis || []),
    [rfis]
  );

  const auditorFilterOptions = useMemo(
    () =>
      (auditors || []).map((a) => ({
        value: a.vqa_auditor_id,
        label: a.vqa_auditor_name,
        vqa_auditor_id: a.vqa_auditor_id,
        vqa_auditor_name: a.vqa_auditor_name,
      })),
    [auditors]
  );

  const getToolbarOptionValue = useCallback((sectionId, option) => {
    if (sectionId === "auditor") {
      return (
        option?.vqa_auditor_id ?? option?.id ?? option?.value ?? undefined
      );
    }
    return defaultGetOptionValue(sectionId, option);
  }, []);

  const filters = useMemo(
    () => [
      {
        id: "sponsor",
        name: "Sponsor",
        searchable: true,
        options: sponsors || [],
        total: sponsors?.length || 0,
      },
      // TODO: uncomment to display type and GXP filters
      // {
      //   id: "type",
      //   name: "Type",
      //   searchable: true,
      //   options: ASSESSMENT_REQUEST_TYPE_FILTER_OPTIONS,
      //   total: ASSESSMENT_REQUEST_TYPE_FILTER_OPTIONS.length,
      // },
      // {
      //   id: "gxp",
      //   name: "GxP",
      //   searchable: true,
      //   options: MY_RFI_GXP_FILTER_OPTIONS,
      //   total: MY_RFI_GXP_FILTER_OPTIONS.length,
      // },
      {
        id: "services",
        name: "Scope",
        searchable: true,
        isV2: true,
        options: v2ServiceOptions,
        total: v2ServiceOptions?.length || 0,
      },
      {
        id: "onsiteRemote",
        name: "Onsite / Remote",
        searchable: false,
        options: ASSESSMENT_REQUEST_ONSITE_REMOTE_FILTER_OPTIONS,
        total: ASSESSMENT_REQUEST_ONSITE_REMOTE_FILTER_OPTIONS.length,
      },
      {
        id: "auditor",
        name: "Auditor",
        searchable: true,
        options: auditorFilterOptions,
        total: auditorFilterOptions?.length || 0,
      },
      {
        id: "multiSponsor",
        name: "Multi-Sponsor",
        searchable: false,
        options: ASSESSMENT_REQUEST_MULTI_SPONSOR_FILTER_OPTIONS,
        total: ASSESSMENT_REQUEST_MULTI_SPONSOR_FILTER_OPTIONS.length,
      },
      {
        id: "status",
        name: "Status",
        searchable: false,
        options: [
          {
            statusID: "Pending Diligent Review",
            label: "Pending Diligent Review",
            checked: false,
          },
          {
            statusID: "Request Accepted",
            label: "Request Accepted",
            checked: false,
          },
          {
            statusID: "Intro Email Sent",
            label: "Intro Email Sent",
            checked: false,
          },
          {
            statusID: "Intro Meeting Complete",
            label: "Intro Meeting Complete",
            checked: false,
          },
          {
            statusID: "Planning Meeting Complete",
            label: "Planning Meeting Complete",
            checked: false,
          },
          {
            statusID: "Auditor Assigned",
            label: "Auditor Assigned",
            checked: false,
          },
          {
            statusID: "Assessment Scheduled",
            label: "Assessment Scheduled",
            checked: false,
          },
          {
            statusID: "Assessment in Progress",
            label: "Assessment in Progress",
            checked: false,
          },
          {
            statusID: "Assessment Complete",
            label: "Assessment Complete",
            checked: false,
          },
          {
            statusID: "Draft Review by Client",
            label: "Draft Review by Client",
            checked: false,
          },
          {
            statusID: "Final Report Delivered",
            label: "Final Report Delivered",
            checked: false,
          },
          {
            statusID: "Report Published to Library",
            label: "Report Published to Library",
            checked: false,
          },
          { statusID: "Cancelled", label: "Cancelled", checked: false },
        ],
        total: 5,
      },
    ],
    [sponsors, v2ServiceOptions, auditorFilterOptions]
  );

  const serverSortParams = useMemo(() => {
    if (!VQA_LIST_USE_SERVER_SORT) return undefined;
    return sortBackendAssessmentRequestConfigToApiSortParams(sortConfig);
  }, [sortConfig]);

  const {
    data: requestList,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetAllVQAList(
    currentPage,
    selectedIds.auditor,
    [],
    selectedIds.sponsor,
    getVqaListStatusFilterPayload(selectedIds.status),
    selectedIds.services,
    undefined,
    searchQuery,
    21,
    serverSortParams,
    sortFetchNonce
  );

  const normalizedItems = useMemo(
    () =>
      (requestList?.items || []).map(normalizeVqaListItemForAssessmentGrid),
    [requestList?.items]
  );

  const sortedItems = useMemo(() => {
    const afterFilters = filterAssessmentRequestItemsByClientFilters(
      normalizedItems,
      selectedIds,
      searchQuery
    );
    if (VQA_LIST_USE_SERVER_SORT) {
      return afterFilters;
    }
    return sortBackendAssessmentRequestItems(afterFilters, sortConfig);
  }, [normalizedItems, selectedIds, searchQuery, sortConfig]);

  const exportDataRef = useRef(null);
  const handleExportDataReady = useCallback((fn) => {
    exportDataRef.current = fn;
  }, []);

  useEffect(() => {
    const getData = () => {
      const records = sortedItems.map((item) =>
        mapBackendAssessmentRequestItemToExportRecord(item)
      );
      return {
        records,
        headers: [...BACKEND_ASSESSMENT_REQUEST_EXPORT_HEADERS],
      };
    };
    handleExportDataReady(getData);
  }, [sortedItems, handleExportDataReady]);

  const hasToolbarFilterSelection = Object.entries(selectedIds).some(
    ([key, arr]) =>
      key !== "search" && Array.isArray(arr) && arr.length > 0
  );

  return (
    <div className="w-full min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 pb-6">
      <div className="shadow-header -mx-4 sm:-mx-6 lg:-mx-8 mb-6">
        <div className="bg-white py-4 flex flex-col gap-4 px-4 sm:px-6 lg:px-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="sm:flex-auto min-w-0">
            <h1 className="text-lg font-medium text-gray-900">
              My Assessments
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              The below table displays all active and historical assessment
              requests.
            </p>
          </div>
          <div className="flex shrink-0 justify-end mt-4">
            <TableExportMenu
              getData={() =>
                exportDataRef.current
                  ? exportDataRef.current()
                  : { records: [], headers: [] }
              }
              reportTitle="Vendor Assessments"
              reportSubtitle="All active and historical assessment requests"
              filePrefix="Vendor_Assessments"
              downloadType="Assessment Requests"
              cellStyles={assessmentCellStyles}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 w-full min-w-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between w-full min-w-0">
          <div className="flex w-full min-w-0 flex-1 flex-col gap-3 lg:max-w-xl">
            <ToolbarSearch
              value={searchQuery}
              onDebouncedChange={(text) =>
                dispatch(
                  setSearchTextByPage({
                    pageKey: PROVIDER_ASSESSMENT_PAGE_KEY,
                    searchText: text,
                  })
                )
              }
              placeholder="Search your assessment details..."
            />
          </div>
          <div className="w-full min-w-0 lg:w-auto lg:flex-shrink-0">
            <PageToolbarFilters
              sections={filters}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              serviceCategoryV2SourceData={rfis || []}
              getOptionValue={getToolbarOptionValue}
            />
          </div>
        </div>
      </div>

      <div className="w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="px-4 py-6 sm:px-6">
            <SkeletonLoader />
          </div>
        ) : (
          <BackendAssessmentRequestGridTable
            data={sortedItems}
            sortConfig={sortConfig}
            onSort={handleSort}
            refetch={refetch}
            isSearchActive={
              !!searchQuery || hasToolbarFilterSelection
            }
            showSponsorColumn={true}
            showVendorColumn={false}
            actionVariant="provider"
            collapseReportStatuses={false}
          />
        )}

        {!isError && !isLoading && sortedItems.length > 0 && (
          <div className="border-t border-gray-200">
            <Pagination
              totalPages={requestList?.total_pages || 1}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      <Notification
        show={show}
        setShow={setShow}
        errorMessage={error && error.message}
        status="error"
      />
    </div>
  );
};
